import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { IAppState } from 'shared/common/state';
import { TOffersProps } from 'shared/containers/OffersContainer/types';
import { selectAllOfferChatOnboardings } from 'shared/selectors/chatOnboarding';
import { getTrackingData } from 'shared/selectors/getTrackingData';
import { getOffers } from 'shared/selectors/offers';
import {
  selectAggregatedOffers,
  selectCurrentPageNumber,
  selectJsonQuery,
  selectQueryString,
  selectResults,
  selectTotalOffers,
  selectSuggestions,
} from 'shared/selectors/results';
import { selectAutoScrollSettings } from 'shared/selectors/selectAutoScrollSettings';
import { selectBreadcrumbs } from 'shared/selectors/selectBreadcrumbs';
import { selectComparisonOffers } from 'shared/selectors/selectComparisonOffers';
import { selectServicesBannerPositions } from 'shared/selectors/selectServicesBannerPositions';
import { selectSuburbanBuildersProjects } from 'shared/selectors/suburbanBuildersProjects';
import { getUser } from 'shared/selectors/user';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { isGoogleBot, isYandexBot } from 'shared/utils/user_agent';

const selectCurrentPath = (state: IAppState) => state.currentPath;
const selectIsPrintEnabled = (state: IAppState) => state.print.enabled;
const selectUserAgent = (state: IAppState) => state.userAgent;
const selectSimplifiedOfferCardPopupsState = (state: IAppState) => state.simplifiedOfferCardPopupsState;
const selectShownId = (state: IAppState) => state.loginMotivationPopupOnFavorite.shownId;
const selectSuggestionDistancesSeoText = (state: IAppState) => state.suggestionDistancesSeoText;
const selectHideOffer = (state: IAppState) => state.hideOffer;
const selectMaxAuctionBet = (state: IAppState) => state.maxAuctionBet;
const selectMaxAuctionService = (state: IAppState) => state.maxAuctionService;
const selectMlRankingGuid = (state: IAppState) => state.mlRankingGuid;
const selectMlRankingModelVersion = (state: IAppState) => state.mlRankingModelVersion;
const selectComplaintsFormStatus = (state: IAppState) => state.complaintFeedbackFrom.status;
const selectExcludedUtilitiesTermsRegions = (state: IAppState) => state.excludedUtilitiesTermsRegions;
const selectFeatureToggle = (state: IAppState) => state.featureToggle;
const selectAbUseExperiments = (state: IAppState) => state.abUseExperiments;
const selectAuctionBanks = (state: IAppState) => state.auctionBanks;
const selectVillagePromotionLabel = (state: IAppState) => state.villagePromotionLabel;

export const useOfferProps = (): TOffersProps => {
  const { logger } = useApplicationContext();

  // Shared selectors
  const queryString = useSelector(selectQueryString);
  const offersQty = useSelector(selectTotalOffers);
  const breadcrumbs = useSelector(selectBreadcrumbs);
  const currentPageNumber = useSelector(selectCurrentPageNumber);
  const jsonQuery = useSelector(selectJsonQuery);
  const offers = useSelector(getOffers);
  const aggregatedOffersCount = useSelector(selectAggregatedOffers);
  const user = useSelector(getUser);
  const comparison = useSelector(selectComparisonOffers);
  const trackingData = useSelector(getTrackingData);
  const suburbanBuildersProjects = useSelector(selectSuburbanBuildersProjects);
  const allOfferChatOnboardings = useSelector(selectAllOfferChatOnboardings);
  const servicesBannerPositions = useSelector(selectServicesBannerPositions);
  const suggestions = useSelector(selectSuggestions);
  const autoScrollSettings = useSelector(selectAutoScrollSettings);

  const {
    commentingBlockedOffers,
    commentingBlockedAgents,
    commentingErroneousOffers,
    commentingErroneousAgents,
    qsToUris,
    ratingBlockedAgents,
    extendedOffersCount,
  } = useSelector(selectResults);

  // Local selectors
  const currentPath = useSelector(selectCurrentPath);
  const isPrintEnabled = useSelector(selectIsPrintEnabled);
  const userAgent = useSelector(selectUserAgent);
  const simplifiedOfferCardPopupsState = useSelector(selectSimplifiedOfferCardPopupsState);
  const shownId = useSelector(selectShownId);
  const suggestionDistancesSeoText = useSelector(selectSuggestionDistancesSeoText);
  const hideOffer = useSelector(selectHideOffer);
  const maxAuctionBet = useSelector(selectMaxAuctionBet);
  const maxAuctionService = useSelector(selectMaxAuctionService);
  const mlRankingGuid = useSelector(selectMlRankingGuid);
  const mlRankingModelVersion = useSelector(selectMlRankingModelVersion);
  const complaintsFormStatus = useSelector(selectComplaintsFormStatus);
  const excludedUtilitiesTermsRegions = useSelector(selectExcludedUtilitiesTermsRegions);
  const featureToggle = useSelector(selectFeatureToggle);
  const abUseExperiments = useSelector(selectAbUseExperiments);
  const auctionBanks = useSelector(selectAuctionBanks);
  const villagePromotionLabel = useSelector(selectVillagePromotionLabel);

  const isBot = useMemo(() => isGoogleBot(userAgent) || isYandexBot(userAgent), [userAgent]);

  const isPopupsOpened = useCallback(() => {
    const popupsStateKeys = Object.keys(simplifiedOfferCardPopupsState).map(Number);

    return popupsStateKeys.some(offerId => Boolean(simplifiedOfferCardPopupsState[offerId].length));
  }, [simplifiedOfferCardPopupsState]);

  return useMemo(
    () => ({
      comparison,
      abUseExperiments,
      mlRankingModelVersion,
      featureToggle,
      servicesBannerPositions,
      suburbanBuildersProjects,
      // количество всех и расширенных офферов, используем для вычисления позиции в выдаче
      aggregatedOffersCount,
      extendedOffersCount,
      offersQty,
      breadcrumbs,
      commentingBlockedOffers,
      commentingBlockedAgents,
      commentingErroneousOffers,
      commentingErroneousAgents,
      currentPageNumber,
      currentPath,
      isBot,
      isPrintEnabled,
      isPopupsOpened,
      jsonQuery,
      logger,
      offers,
      queryString,
      maxAuctionBet,
      maxAuctionService,
      qsToUris,
      ratingBlockedAgents,
      shownId,
      suggestionDistancesSeoText,
      user,
      hideOffer,
      // лимит избранного для неавторизованного юзера
      favoritesLimitForUnauthUser: 5,
      mlRankingGuid,
      complaintsFormStatus,
      excludedUtilitiesTermsRegions,
      trackingData,
      suggestions,
      allOfferChatOnboardings,
      auctionBanks,
      villagePromotionLabel,
      autoScrollSettings,
    }),
    [
      comparison,
      abUseExperiments,
      mlRankingModelVersion,
      featureToggle,
      servicesBannerPositions,
      suburbanBuildersProjects,
      aggregatedOffersCount,
      extendedOffersCount,
      offersQty,
      breadcrumbs,
      commentingBlockedOffers,
      commentingBlockedAgents,
      commentingErroneousOffers,
      commentingErroneousAgents,
      currentPageNumber,
      currentPath,
      isBot,
      isPrintEnabled,
      isPopupsOpened,
      jsonQuery,
      logger,
      offers,
      queryString,
      maxAuctionBet,
      maxAuctionService,
      qsToUris,
      ratingBlockedAgents,
      shownId,
      suggestionDistancesSeoText,
      user,
      hideOffer,
      mlRankingGuid,
      complaintsFormStatus,
      excludedUtilitiesTermsRegions,
      trackingData,
      suggestions,
      allOfferChatOnboardings,
      auctionBanks,
      villagePromotionLabel,
      autoScrollSettings,
    ],
  );
};
